import type { SxProps, Theme, TypographyVariant } from '@mui/material/styles';

/**
 * Menu width in pixels.
 *
 * @remarks
 * Fixed width ensures consistent menu appearance across different screen sizes.
 * Width accounts for horizontal padding on content sections.
 */
export const menuWidth = 340;

/**
 * Standard horizontal padding for menu sections.
 *
 * @remarks
 * Using theme spacing: 2 = 16px with default 8px spacing unit.
 */
export const menuHorizontalPadding = 2;

/**
 * Standard vertical padding for menu sections.
 *
 * @remarks
 * Using theme spacing: 1.5 = 12px with default 8px spacing unit.
 */
export const menuVerticalPadding = 1.5;

/**
 * Typography variant used by Datapad menu section labels.
 *
 * @remarks
 * Using `'overline'` provides a subtle, uppercase label style that creates
 * clear visual hierarchy without competing with content.
 */
export const menuLabelTypographyVariant: TypographyVariant = 'overline';

/**
 * Sx props for Datapad menu section labels.
 *
 * @remarks
 * **MUI Pattern: Shared `sx` styles as typed constants**
 *
 * Section labels use:
 * - Overline typography for subtle visual hierarchy
 * - Muted text color (text.secondary) to not compete with main content
 * - Consistent margin for spacing from content below
 *
 * @see {@link https://mui.com/system/getting-started/the-sx-prop/}
 */
export const menuLabelSx: SxProps<Theme> = {
	color: 'text.secondary',
	letterSpacing: '0.1em',
	mb: 1,
};

/**
 * Sx props for menu sections (groups of related content).
 *
 * @remarks
 * Each section has consistent padding and full width to maintain
 * visual rhythm throughout the menu. Box-sizing ensures padding
 * is included in the width calculation.
 */
export const menuSectionSx: SxProps<Theme> = {
	px: menuHorizontalPadding,
	py: menuVerticalPadding,
	width: '100%',
	boxSizing: 'border-box',
	overflow: 'hidden',
};

/**
 * Border radius for interactive menu items.
 *
 * @remarks
 * Rounded corners on buttons and selections create a softer, more modern feel.
 */
export const menuItemBorderRadius = 1.5;
